import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CancelOutlined,
  MoreHoriz,
} from "@mui/icons-material";
import {
  apiCallDietaryList,
  apiCallDietaryAdd,
  apiCallDietaryUpdate,
  apiCallDietaryStatus,
} from "../../../../Core/Apicall";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { clearShowMenu } from '../../../../Redux/Reducers/GlobalVar'
import DeletePopup from "../../../Common/DeletePopup/DeletePopup";
import CommonMenu from "../../../Common/CommonMenu/CommonMenu";
import CommonStatus from "../../../Common/CommonStatus/CommonStatus";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import { toast } from 'react-toastify';

interface DietaryTypes {
  _id?: string;
  dietaryName: string;
  displayOrder: number | string;
  isActive: boolean;
  isDelete: boolean;
}

function Dietary() {
  const [data, setData] = useState<DietaryTypes[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [search, setSearch] = useState<string>("");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const showMenu: any = useSelector((state: RootState) => state.global.showMenu);
  const [isCreateNew, setIsCreateNew] = useState<boolean>(false);
  useEffect(() => {
    setIsCreateNew(!!showMenu);
  }, [showMenu]);

  const [formData, setFormData] = useState<DietaryTypes>({
    _id: "",
    dietaryName: "",
    displayOrder: "",
    isActive: true,
    isDelete: false,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVibe, setMenuVibe] = useState<DietaryTypes | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchDietary = async () => {
      setLoading(true);
      try {
        const response = await apiCallDietaryList("");
        if (response?.data) {
          setData(response.data);
        } else {
          setData([]);
        }
      } catch (err: any) {
      } finally {
        setLoading(false);
      }
    };
    fetchDietary();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (formData._id) {
        const data = {
          id: formData._id,
          dietaryName: formData?.dietaryName,
          displayOrder: Number(formData.displayOrder)
        }
        await apiCallDietaryUpdate(data);
        toast.success("Dietary updated successfully!");
      } else {
        await apiCallDietaryAdd(formData);
        toast.success("Dietary saved successfully!");
      }

      setIsCreateNew(false);

      setFormData({
        _id: "",
        dietaryName: "",
        displayOrder: "",
        isActive: true,
        isDelete: false,
      });
      const response = await apiCallDietaryList("");
      setData(response?.data || []);
    } catch (err) {
      toast.error("Failed to save Dietary");
    }
  };

  const handleStatusUpdate = async (item: DietaryTypes) => {
    try {
      await apiCallDietaryStatus({
        id: item._id,
        isActive: !item.isActive,
      });
      setData((prev) =>
        prev.map((m) =>
          m._id === item._id ? { ...m, isActive: !item.isActive } : m
        )
      );
      setStatusDialogOpen(false)
    } catch (err) {
    }
  };

  const handleDelete = async () => {
    if (!menuVibe) return
    try {
      await apiCallDietaryStatus({
        id: menuVibe._id,
        isDelete: true,
      })
      setData(prev => prev.filter(item => item._id !== menuVibe._id))
    } catch (err) {
    } finally {
      setDeleteDialogOpen(false)
      setMenuVibe(null)
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, type: any) => {
    setAnchorEl(event.currentTarget)
    setMenuVibe(type)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVibe) {
          setFormData({
            ...menuVibe,
            _id: menuVibe._id,
          });
          setIsCreateNew(true);
        }
        handleMenuClose();
      },
    },
    {
      label: "Delete",
      onClick: () => setDeleteDialogOpen(true),
    }

  ];


  return (
    <Box style={{ paddingBottom: "20px" }}>

      <Box className="d-flex input align-items-center justify-content-between">
        <TextField
          className="border-10"
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
          sx={{ backgroundColor: "white" }}
          placeholder="Search Dietary"
        />
        <Button
          className="btn-black p-2 px-3"
          onClick={() => {
            setFormData({
              _id: "",
              dietaryName: "",
              displayOrder: "",
              isActive: true,
              isDelete: false,
            })
            setIsCreateNew(true)
          }}
        >
          + Create New
        </Button>
      </Box>


      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        }}
      >

        <TableContainer className="scroll-bar">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Display Order</TableCell>
                <TableCell>Dietary Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data
                .filter(
                  (item) =>
                    search === "" ||
                    item.dietaryName.toLowerCase().includes(search.toLowerCase())
                )
                .map((item, index: any) => (
                  <TableRow hover key={index}>
                    <TableCell>{item.displayOrder}</TableCell>
                    <TableCell>{item.dietaryName}</TableCell>
                    <TableCell>
                      <Button
                        className={item.isActive ? "btn-success" : "btn-danger"}
                        onClick={() => setStatusDialogOpen(index)}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </Button>
                      <CommonStatus
                        open={statusDialogOpen === index}
                        title={item?.dietaryName}
                        message={`Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this venue?`}
                        confirmText={item?.isActive ? "Deactivate" : "Activate"}
                        confirmColor={item?.isActive ? "warning" : "success"}
                        onConfirm={() => handleStatusUpdate(item)}
                        onClose={() => setStatusDialogOpen(false)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e: any) => handleMenuOpen(e, item)}>
                        <MoreHoriz />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {loading &&
            <div style={{ padding: "10px 10px" }}>
              <CommonShimmer />
            </div>}
        </TableContainer>

      </Paper>

      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />

      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this dietary?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />


      <Drawer
        anchor="right"
        open={isCreateNew}
        onClose={() => {
          setIsCreateNew(false)
          dispatch(clearShowMenu());
        }}
        PaperProps={{
          sx: { width: "35%", borderRadius: "16px 0 0 16px", overflow: "hidden" },
        }}
      >
        <Box
          className="day-form"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          {/* ---------- Header ---------- */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={2}
            borderBottom="1px solid #eee"
            bgcolor="#fafafa"
          >
            <Typography variant="h6" fontWeight={600}>
              {formData._id ? "Edit Dietary" : "Create Dietary"}
            </Typography>
            <IconButton onClick={() => {
              setIsCreateNew(false)
              dispatch(clearShowMenu());
            }
            }>
              <CancelOutlined />
            </IconButton>
          </Box>

          {/* ---------- Form ---------- */}
          <Box flex={1} px={3} py={2} overflow="auto">
            <form onSubmit={handleSubmit}>
              {/* Dietary Name */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Dietary Name
                </Typography>
                <TextField
                  name="dietaryName"
                  value={formData.dietaryName}
                  onChange={handleChange}
                  placeholder="Enter Dietary Name"
                  fullWidth
                />
              </FormControl>

              {/* Display Order (Dietary Index) */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Display Order
                </Typography>
                <TextField
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  placeholder="Enter Display Order"
                  fullWidth
                />
              </FormControl>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#222" },
                }}
              >
                Save
              </Button>
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box >
  );
}

export default Dietary;
